/**
 * Putting the signature on the page.
 *
 * The mark is drawn onto the page itself, not left as an interactive
 * annotation, and the field it sat on is removed first. Both matter: a
 * signature that is still a form field is a signature somebody can select and
 * delete, and a PDF viewer that decides not to render annotations would print
 * the packet with every signature missing and no warning.
 *
 * Order is load → fill → stamp → flatten, which is why this runs inside
 * fillForm rather than over the finished bytes. Once a form is flattened its
 * fields are gone and there is nothing left to measure against.
 */

import { type PDFDocument } from "pdf-lib";
import { anchorsFor, type Anchor, type SignerRole } from "./anchors";

export interface Mark {
  /** The drawn signature, as a PNG data URL. */
  image: string;
  /** Initials, where a form asks for them separately. Falls back to image. */
  initials?: string | null;
}

export type Marks = Partial<Record<SignerRole, Mark>>;

export interface StampReport {
  placed: { role: SignerRole; where: string }[];
  /** An anchor whose field is not in this template. Usually means the state
      reissued the form; always worth surfacing rather than swallowing. */
  missing: { role: SignerRole; where: string; why: string }[];
}

const dataUrlToBytes = (dataUrl: string): Uint8Array => {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/**
 * Fit a signature inside its box without distorting it.
 *
 * A signature stretched to fill the box is not the signature the person drew,
 * and on a document whose point is that this person made this mark, that is
 * not a cosmetic problem. Aspect ratio is preserved.
 */
function fit(
  imgW: number, imgH: number,
  box: { x: number; y: number; width: number; height: number },
  /** Room reserved around the mark for the dilation ring; see stampMark. */
  inset = 0,
) {
  const padX = Math.min(6, box.width * 0.04);
  const padY = Math.max(1, box.height * 0.08);
  const maxW = box.width - padX * 2 - inset * 2;
  const maxH = box.height - padY * 2 - inset * 2;

  /* Strictly inside the box, never over it. An earlier version let the mark
     stand proud of the box the way handwriting ascends above a ruled line,
     which looked right on a wide field and printed straight through the
     "Seller's Signature" caption on the 82993, whose cell is 18 points tall
     with the caption inside it. On a form going to a tax collector, a mark
     that fits is worth more than a mark that flourishes. */
  const scale = Math.min(maxW / imgW, maxH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return {
    x: box.x + padX + inset,
    y: box.y + (box.height - h) / 2,
    width: w,
    height: h,
    /** How far the capture was shrunk; the ring radius is derived from it. */
    scale,
  };
}

/* ------------------------------------------------------------------ */
/* Making the mark survive paper                                       */
/* ------------------------------------------------------------------ */

/**
 * The signature as captured is far thinner on paper than it looks on screen.
 *
 * The pad draws a 2.4 CSS-pixel stroke, which on a retina screen is about five
 * device pixels of a roughly 900-pixel-wide capture. The signature cell on an
 * 82993 is 262 x 19 points, and preserving the aspect ratio of a 916 x 355
 * capture inside 19 points means scaling it to about 4.5 per cent. Five pixels
 * at 4.5 per cent is 0.22 points — thinner than the 0.25-point hairline that
 * print shops warn will drop out, and this document gets printed, signed over,
 * scanned and faxed to a tax collector before anyone needs to read it.
 *
 * Thickening the pen at capture cannot fix it: it would take a stroke like a
 * marker on screen to survive a twenty-fold reduction, and the loops of a
 * signature fill in. Enlarging the mark cannot fix it either — the cell really
 * is that short, and an earlier version that let the mark stand proud of the
 * box printed through the "Seller's Signature" caption.
 *
 * So the mark is drawn several times around a small ring. Every pass is the
 * identical artwork, so nothing about the shape changes; the strokes simply
 * gain the ring's diameter in width. Measured against the real form with a
 * real signature, a 0.4-point radius lands near 0.9 points — a pen line — while
 * 0.8 begins to fill the loops of an 'a'.
 */
const RING: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [0.7, 0.7], [-0.7, 0.7], [0.7, -0.7], [-0.7, -0.7],
];

/** Points of stroke to aim for once the capture has been shrunk. */
const TARGET_STROKE_PT = 0.9;
/** What the pad lays down, in device pixels: 2.4 CSS px at a 2x ratio. */
const CAPTURED_STROKE_PX = 4.8;

/**
 * Ring radius for a given reduction.
 *
 * Derived rather than fixed, because the boxes differ: a roomy field on the
 * purchase agreement shrinks the capture far less than the 19-point cell on an
 * 82993, and a radius tuned for the tight one would ink the generous one into
 * a smudge. Clamped at both ends — enough to matter, never enough to blot.
 */
function ringRadius(scale: number): number {
  const native = CAPTURED_STROKE_PX * scale;
  if (native >= TARGET_STROKE_PT) return 0;        // already a real line
  return Math.min(0.5, Math.max(0.12, (TARGET_STROKE_PT - native) / 2));
}

/**
 * Stamp every signature this document calls for.
 *
 * Must be called on a filled but NOT yet flattened document.
 */
export async function stampSignatures(
  doc: PDFDocument,
  docKey: string,
  marks: Marks,
): Promise<StampReport> {
  const report: StampReport = { placed: [], missing: [] };
  const anchors = anchorsFor(docKey);
  if (anchors.length === 0) return report;

  const form = doc.getForm();
  const pages = doc.getPages();

  // Embed each distinct image once, however many places it appears.
  const embedded = new Map<string, Awaited<ReturnType<typeof doc.embedPng>>>();
  const embed = async (dataUrl: string) => {
    let img = embedded.get(dataUrl);
    if (!img) {
      img = await doc.embedPng(dataUrlToBytes(dataUrl));
      embedded.set(dataUrl, img);
    }
    return img;
  };

  for (const anchor of anchors) {
    const mark = marks[anchor.role];
    const where = anchor.field ?? `page ${anchor.page + 1}`;

    // No co-buyer on this deal, or the dealer has not countersigned yet.
    if (!mark) continue;

    const page = pages[anchor.page];
    if (!page) {
      report.missing.push({
        role: anchor.role, where,
        why: `this template has ${pages.length} page(s), anchor wants page ${anchor.page + 1}`,
      });
      continue;
    }

    let box: { x: number; y: number; width: number; height: number } | null = null;

    if (anchor.field) {
      const field = form.getFieldMaybe(anchor.field);
      if (!field) {
        report.missing.push({
          role: anchor.role, where,
          why: "no such field in this template",
        });
        continue;
      }
      const widget = field.acroField.getWidgets()[0];
      if (!widget) {
        report.missing.push({ role: anchor.role, where, why: "field has no widget" });
        continue;
      }
      const r = widget.getRectangle();
      box = { x: r.x, y: r.y, width: r.width, height: r.height };

      /* Take the field out before drawing. Left in place, flattening would
         paint the field's own appearance — a border, a background — straight
         over the signature. */
      try { form.removeField(field); } catch { /* already gone; the draw still stands */ }
    } else if (anchor.rect) {
      const [x0, y0, x1, y1] = anchor.rect;
      box = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    }

    if (!box) {
      report.missing.push({ role: anchor.role, where, why: "anchor has neither field nor rect" });
      continue;
    }

    const source = anchor.initials ? (mark.initials || mark.image) : mark.image;
    const img = await embed(source);

    /* Measured once without an inset to learn the reduction, then laid out
       again with room for the ring, so the dilated mark still sits strictly
       inside the box. */
    const radius = ringRadius(fit(img.width, img.height, box).scale);
    const placed = fit(img.width, img.height, box, radius);
    const { scale: _unused, ...rect } = placed;

    if (radius > 0) {
      for (const [dx, dy] of RING) {
        page.drawImage(img, {
          ...rect, x: rect.x + dx * radius, y: rect.y + dy * radius,
        });
      }
    } else {
      page.drawImage(img, rect);
    }

    report.placed.push({ role: anchor.role, where });
  }

  return report;
}

/** sha256 of the finished bytes, hex. What makes a copy provable later. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Short, readable form of a hash for printing on a certificate page. */
export const shortHash = (hex: string) =>
  (hex.match(/.{1,4}/g) ?? []).slice(0, 8).join(" ").toUpperCase();

export type { Anchor };
